import React, { useState } from 'react';
import PortalLayout from './PortalLayout';
import LeaderboardList from '../../components/LeaderboardList';
import WeeklyMovers from '../../components/WeeklyMovers';
import LeaderboardShareCard from '../../components/LeaderboardShareCard';
import { Trophy, ChevronLeft, Globe, MapPin, Share2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const WINDOWS = [
  { id: 'all',   label: 'All-time' },
  { id: 'month', label: 'This month' },
  { id: 'week',  label: 'This week' },
];

const PortalLeaderboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const cityName = user?.destination_city || null;
  const cityLabel = cityName ? `Top in ${cityName}` : 'Top in your city';
  const [activeScope, setActiveScope] = useState('global');
  const [activeWindow, setActiveWindow] = useState('all');
  const [shareOpen, setShareOpen] = useState(false);
  const [viewerRow, setViewerRow] = useState(null);

  return (
    <PortalLayout activeTab="community">
      <div className="bg-[#F9F6EF] min-h-screen" data-testid="leaderboard-page-root">
        <div className="p-4 sm:p-6 lg:p-10 max-w-3xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1 text-[#5A6B73] hover:text-[#0A1F2E] text-sm mb-6 transition"
            data-testid="leaderboard-back-btn"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>

          <header className="flex items-center gap-3 mb-6" data-testid="leaderboard-header">
            <div className="w-11 h-11 bg-[#E8A830]/15 rounded-full flex items-center justify-center">
              <Trophy className="w-5 h-5 text-[#E8A830]" />
            </div>
            <div>
              <h1 className="font-display italic text-3xl text-[#0A1F2E] leading-tight">
                Leaderboard
              </h1>
              <p className="text-[#5A6B73] text-sm">
                {activeScope === 'global'
                  ? 'Top members by points'
                  : `Top members in ${cityName}`}
                {' · '}
                {WINDOWS.find((w) => w.id === activeWindow)?.label}
              </p>
            </div>
          </header>

          {/* Scope tabs */}
          <div
            className="flex items-center gap-2 mb-3 bg-white rounded-full p-1 border border-[#EFE6CF] w-fit"
            role="tablist"
            data-testid="leaderboard-tabs"
          >
            <button
              type="button"
              role="tab"
              aria-selected={activeScope === 'global'}
              onClick={() => setActiveScope('global')}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full transition ${
                activeScope === 'global'
                  ? 'bg-[#0A1F2E] text-white'
                  : 'text-[#5A6B73] hover:text-[#0A1F2E]'
              }`}
              data-testid="leaderboard-tab-global"
            >
              <Globe className="w-4 h-4" /> Global
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeScope === 'city'}
              onClick={() => cityName && setActiveScope('city')}
              disabled={!cityName}
              title={cityName ? undefined : 'Add your destination city in profile to unlock the city leaderboard'}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full transition ${
                activeScope === 'city'
                  ? 'bg-[#0A1F2E] text-white'
                  : cityName
                  ? 'text-[#5A6B73] hover:text-[#0A1F2E]'
                  : 'text-[#5A6B73]/40 cursor-not-allowed'
              }`}
              data-testid="leaderboard-tab-city"
            >
              <MapPin className="w-4 h-4" /> {cityLabel}
            </button>
          </div>

          {/* Time window toggle */}
          <div
            className="flex items-center gap-2 mb-5 text-xs font-mono uppercase tracking-wider"
            role="tablist"
            data-testid="leaderboard-windows"
          >
            {WINDOWS.map((w) => (
              <button
                key={w.id}
                type="button"
                role="tab"
                aria-selected={activeWindow === w.id}
                onClick={() => setActiveWindow(w.id)}
                className={`px-3 py-1.5 rounded-full border transition ${
                  activeWindow === w.id
                    ? 'border-[#E8A830] bg-[#E8A830]/15 text-[#8C5B12] font-semibold'
                    : 'border-[#EFE6CF] text-[#5A6B73] hover:border-[#2CA8A0]/40 hover:text-[#0A1F2E]'
                }`}
                data-testid={`leaderboard-window-${w.id}`}
              >
                {w.label}
              </button>
            ))}
          </div>

          <WeeklyMovers variant="full" />

          {viewerRow && (
            <div className="mb-4 flex justify-end">
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#0A1F2E] text-white text-xs font-mono uppercase tracking-wider font-semibold hover:brightness-110 transition"
                data-testid="leaderboard-share-rank-btn"
              >
                <Share2 className="w-3.5 h-3.5" />
                Share my rank
              </button>
            </div>
          )}

          <div
            className="bg-white border border-[#EFE6CF] rounded-2xl overflow-hidden shadow-sm"
            data-testid="leaderboard-full-list"
          >
            <LeaderboardList
              limit={20}
              scope={activeScope}
              cityFilter={activeScope === 'city' ? 'me' : null}
              window={activeWindow}
              onViewerRow={setViewerRow}
            />
          </div>

          <p className="text-[#5A6B73] text-xs mt-4 leading-relaxed">
            Earn points by completing lessons (+10), posting (+10), commenting (+5),
            receiving likes (+2), and logging in daily (+3).
            {activeScope === 'city' && cityName && (
              <> · Showing members heading to <span className="font-medium text-[#0A1F2E]">{cityName}</span>.</>
            )}
            {activeWindow !== 'all' && (
              <> · Points counted from the last {activeWindow === 'week' ? '7 days' : '30 days'}.</>
            )}
          </p>
        </div>
      </div>
      <LeaderboardShareCard
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        rank={viewerRow?.rank ?? '-'}
        points={viewerRow?.points ?? 0}
        cityName={activeScope === 'city' ? cityName : null}
        firstName={user?.first_name || viewerRow?.first_name}
        windowLabel={
          activeWindow === 'week' ? 'this week'
            : activeWindow === 'month' ? 'this month'
            : 'all time'
        }
      />
    </PortalLayout>
  );
};

export default PortalLeaderboard;
